// Modal dialog for bulk markdown export.

import React, { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import type { ChatSession, ChatFolder } from '../../types';
import {
  bulkExportToMarkdown,
  triggerDownload,
  formatDate,
  type BulkExportScope,
} from '../../lib/export';

interface ExportDialogProps {
  sessions: ChatSession[];
  folders: ChatFolder[];
  initialScope?: BulkExportScope;
  onClose: () => void;
}

export default function ExportDialog({
  sessions,
  folders,
  initialScope,
  onClose,
}: ExportDialogProps) {
  const [scope, setScope] = useState<BulkExportScope>(initialScope ?? { type: 'all' });
  const [detailed, setDetailed] = useState(false);
  const overlayRef = useRef<HTMLDivElement>(null);

  // Close on Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  // Close on overlay click
  const handleOverlayClick = useCallback((e: React.MouseEvent) => {
    if (e.target === overlayRef.current) onClose();
  }, [onClose]);

  // Count chats per scope
  const countForScope = (s: BulkExportScope): number => {
    if (s.type === 'all') return sessions.length;
    if (s.type === 'folder') return sessions.filter((x) => x.folderId === s.folderId).length;
    return sessions.filter((x) => !x.folderId).length;
  };

  const chatCount = countForScope(scope);

  const filename = `malpediarag-export-${formatDate()}.md`;

  const handleDownload = useCallback(() => {
    const md = bulkExportToMarkdown(sessions, folders, scope, detailed);
    triggerDownload(md, filename);
    onClose();
  }, [sessions, folders, scope, detailed, filename, onClose]);

  const scopeKey = (s: BulkExportScope): string => {
    if (s.type === 'all') return 'all';
    if (s.type === 'folder') return `folder-${s.folderId}`;
    return 'unfiled';
  };

  const isScopeSelected = (s: BulkExportScope): boolean => scopeKey(s) === scopeKey(scope);

  const unfiledCount = sessions.filter((x) => !x.folderId).length;

  return ReactDOM.createPortal(
    <div
      ref={overlayRef}
      className="malpedia-chat-export-overlay"
      onClick={handleOverlayClick}
    >
      <div className="malpedia-chat-export-modal">
        {/* Header */}
        <div className="malpedia-chat-export-header">
          <span>Export Chats</span>
          <button
            className="malpedia-chat-export-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {/* Body */}
        <div className="malpedia-chat-export-body">
          {/* Scope */}
          <div className="malpedia-chat-export-section">
            <label className="malpedia-chat-export-label">Scope</label>
            <div className="malpedia-chat-export-radios">
              <label className="malpedia-chat-export-radio">
                <input
                  type="radio"
                  name="export-scope"
                  checked={isScopeSelected({ type: 'all' })}
                  onChange={() => setScope({ type: 'all' })}
                />
                All chats ({sessions.length})
              </label>
              {folders.map((f) => {
                const fScope: BulkExportScope = { type: 'folder', folderId: f.id };
                const count = countForScope(fScope);
                return (
                  <label key={f.id} className="malpedia-chat-export-radio">
                    <input
                      type="radio"
                      name="export-scope"
                      checked={isScopeSelected(fScope)}
                      onChange={() => setScope(fScope)}
                    />
                    📂 {f.name} ({count})
                  </label>
                );
              })}
              <label className="malpedia-chat-export-radio">
                <input
                  type="radio"
                  name="export-scope"
                  checked={isScopeSelected({ type: 'unfiled' })}
                  onChange={() => setScope({ type: 'unfiled' })}
                />
                Unfiled ({unfiledCount})
              </label>
            </div>
          </div>

          {/* Detail level */}
          <div className="malpedia-chat-export-section">
            <label className="malpedia-chat-export-label">Detail level</label>
            <div className="malpedia-chat-export-toggle">
              <button
                className={`malpedia-chat-export-toggle-btn${!detailed ? ' active' : ''}`}
                onClick={() => setDetailed(false)}
              >
                Plain
              </button>
              <button
                className={`malpedia-chat-export-toggle-btn${detailed ? ' active' : ''}`}
                onClick={() => setDetailed(true)}
              >
                Detailed
              </button>
            </div>
          </div>

          {/* Filename preview */}
          <div className="malpedia-chat-export-preview">
            {filename}
          </div>
        </div>

        {/* Actions */}
        <div className="malpedia-chat-export-actions">
          <button
            className="malpedia-chat-export-download"
            onClick={handleDownload}
            disabled={chatCount === 0}
          >
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M7 2v8M4 7l3 3 3-3" />
              <path d="M2.5 11.5h9" />
            </svg>
            Download Markdown
            {chatCount > 0 && ` (${chatCount})`}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
